import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TextField, Typography, Stack, Alert } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import AuthApi from "../api/AuthApi";
import { useAuth } from "../context/AuthContext";

const card = { p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" } as const;

/** TOTP enrolment: scan QR → confirm a code → receive one-time recovery codes. */
export default function MfaSetup({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [uri, setUri] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState<string[] | null>(null);

  useEffect(() => {
    AuthApi.mfaSetup().then((r) => { setUri(r.otpauthUri); setSecret(r.secret); }).catch(() => setError(t("mfa.setupError")));
  }, [t]);

  const enable = async () => {
    setError(""); setBusy(true);
    try { const r = await AuthApi.mfaEnable(code.trim()); setRecovery(r.recoveryCodes); }
    catch { setError(t("mfa.badCode")); setBusy(false); }
  };
  const finish = async () => { await refreshUser(); onDone?.(); };

  if (recovery) {
    return (
      <Box sx={card}>
        <Typography variant="h2" sx={{ fontSize: "1.4rem", mb: 1 }}>{t("mfa.recoveryTitle")}</Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>{t("mfa.recoveryBody")}</Alert>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 3, fontFamily: "monospace", fontSize: "1rem" }}>
          {recovery.map((c) => <Box key={c} sx={{ bgcolor: "var(--surface-alt, #f4efe7)", px: 1.5, py: 1, borderRadius: 1, letterSpacing: ".08em" }}>{c}</Box>)}
        </Box>
        <Button variant="contained" onClick={finish}>{t("mfa.done")}</Button>
      </Box>
    );
  }

  return (
    <Box sx={card}>
      <Typography variant="h2" sx={{ fontSize: "1.4rem", mb: 1 }}>{t("mfa.setupTitle")}</Typography>
      <Typography sx={{ color: "text.secondary", mb: 2.5 }}>{t("mfa.setupBody")}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {uri && <Box sx={{ display: "flex", justifyContent: "center", mb: 2, p: 2, bgcolor: "#fff", borderRadius: 2, width: "fit-content", mx: "auto" }}><QRCodeSVG value={uri} size={180} /></Box>}
      {secret && <Typography sx={{ textAlign: "center", fontFamily: "monospace", fontSize: ".85rem", color: "text.secondary", mb: 2.5, wordBreak: "break-all" }}>{t("mfa.manualEntry")}: {secret}</Typography>}
      <Stack spacing={2} component="form" onSubmit={(e) => { e.preventDefault(); enable(); }}>
        <TextField label={t("mfa.code")} value={code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)} inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }} />
        <Button type="submit" variant="contained" size="large" disabled={busy || code.trim().length < 6}>{t("mfa.enable")}</Button>
      </Stack>
    </Box>
  );
}
